'use client';

import * as React from 'react';
import { Button as BaseButton } from '@base-ui/react/button';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';
import { getBaseUiChildren, getBaseUiRenderProp } from '@/components/ui/base-ui-utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control text-sm font-medium transition-colors duration-150 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zen-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-zen-text text-zen-surface shadow-sm transition-colors hover:bg-zen-text/80',
        primary: 'bg-zen-primary text-zen-primary-foreground shadow-sm hover:brightness-95',
        destructive: 'bg-zen-danger text-white shadow-sm hover:brightness-95',
        outline: 'border border-zen-border bg-transparent text-zen-text hover:bg-zen-muted',
        secondary: 'bg-zen-surface text-zen-text shadow-sm hover:bg-zen-muted',
        ghost: 'text-zen-text hover:bg-zen-muted',
        link: 'text-zen-text underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-5 text-sm',
        icon: 'h-9 w-9 px-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends Omit<React.ComponentPropsWithoutRef<typeof BaseButton>, 'className'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  className?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const resolvedVariant = variant ?? 'default';
    const resolvedSize = size ?? 'default';
    const render = getBaseUiRenderProp(asChild, children);
    const buttonChildren = getBaseUiChildren(asChild, children);

    return (
      <BaseButton
        ref={ref}
        render={render}
        className={cn(buttonVariants({ variant: resolvedVariant, size: resolvedSize }), className)}
        {...props}
      >
        {buttonChildren}
      </BaseButton>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
